// http.service.ts
import Axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { toast } from 'sonner'
import { getApiErrorMessage, translateError } from '../utils/error-translations'
import { setAuthNotice } from '../utils/authNotice'

const RAW_BASE_URL = process.env.REACT_APP_API_URL ?? process.env.NEXT_PUBLIC_API_URL
const BASE_URL = typeof RAW_BASE_URL === 'string' ? RAW_BASE_URL.replace(/\/$/, '') : ''

const resolveUrl = (endpoint: string): string => {
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
    if (!BASE_URL) {
        return normalizedEndpoint
    }
    return `${BASE_URL}${normalizedEndpoint}`
}

if (!BASE_URL) {
    console.warn('API base URL env var (REACT_APP_API_URL) is not defined; falling back to relative API URLs')
}

const axios = Axios.create({
    withCredentials: false
})

axios.interceptors.request.use(function (config) {
    if (typeof window !== 'undefined') {
        try {
            const authUserStart = localStorage.getItem('authUser');
            if (authUserStart) {
                const parsed = JSON.parse(authUserStart);
                if (parsed && parsed.token) {
                    config.headers.Authorization = parsed.token;
                }
            }
        } catch (err) {
            // Ignore error
        }
    }
    config.headers['Accept-Language'] = 'he'
    return config
}, function (error) {
    return Promise.reject(error)
})

type RequestConfig = AxiosRequestConfig & {
    getFullResponse?: boolean
    /** The caller shows 400/429 errors itself: no toast. 401 still logs out. */
    silentErrors?: boolean
}

export const http = {
    get<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
        return ajax(endpoint, 'GET', null, config)
    },
    post<T>(endpoint: string, data: unknown = null, config: RequestConfig = {}): Promise<T | any> {
        return ajax(endpoint, 'POST', data, config)
    },
    put<T>(endpoint: string, data: unknown = null, config: RequestConfig = {}): Promise<T> {
        return ajax(endpoint, 'PUT', data, config)
    },
    patch<T>(endpoint: string, data: unknown = null, config: RequestConfig = {}): Promise<T> {
        return ajax(endpoint, 'PATCH', data, config)
    },
    delete<T>(endpoint: string, data: unknown = null, config: RequestConfig = {}): Promise<T> {
        return ajax(endpoint, 'DELETE', data, config)
    }
}

// A 400 carries its business code in `meta.description` (e.g. INVALID_APARTMENT_NUMBER), or — for validation
// errors — the codes of each field in `errors`.
function handleBadRequest(error: any) {
    console.warn('HTTP 400 response received', error)
    toast.error(getApiErrorMessage(error, 'הבקשה נדחתה על ידי השרת'))
    return false
}

const SESSION_ENDED_MESSAGE = translateError('SESSION_EXPIRED')

let isRedirecting = false;

function handleUnauthorized() {
    // Instead of redirecting, log a warning and return a special value
    if (typeof window !== 'undefined') {
        // Clear all auth related data to prevent redirect loops
        localStorage.removeItem('authUser');

        if (window.location.pathname !== '/login' && window.location.pathname !== '/' && !isRedirecting) {
            isRedirecting = true;
            // The session ended: 24 hours after login, another login with the same admin, or a password change.
            toast.warning(SESSION_ENDED_MESSAGE);
            // The login screen repeats it after the reload below.
            setAuthNotice(SESSION_ENDED_MESSAGE);
            // Use a slightly longer timeout to ensure the toast is seen, but short enough to be responsive
            setTimeout(() => {
                // A page opened with an ended session is already on the login screen (the session check logs
                // out), which shows the notice: reloading it would drop the notice.
                if (window.location.pathname === '/login') {
                    isRedirecting = false;
                    return;
                }
                window.location.href = '/login';
            }, 1500);
        }
    }
    console.warn('UNAUTHORIZED: User is not authenticated.');
    return { isUnauthorized: true } as const;
}

async function handleError(error: unknown, { silentErrors = false } = {}): Promise<boolean | { isUnauthorized: true }> {
    if (Axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 400) return silentErrors ? false : handleBadRequest(axiosError);
        // Also where an expired session lands (sessions end 24 hours after login).
        if (axiosError.response?.status === 401) return handleUnauthorized();
        if (axiosError.response?.status === 429) {
            if (!silentErrors) toast.error('(429) יותר מדי בקשות לשרת, נסו שוב מאוחר יותר');
            return false;
        }
        // toast.error('שגיאה בקריאה לשרת');
    }
    return false;
}

async function ajax(endpoint: string, method = 'GET', data: unknown = null, config: RequestConfig = {}) {
    const { getFullResponse, silentErrors, ...axiosConfig } = config;
    try {
        const res = await axios({
            url: resolveUrl(endpoint),
            method,
            data,
            ...axiosConfig
        });
        if (getFullResponse) {
            return res;
        }
        return res.data;
    } catch (err: any) {
        const isHandleError = await handleError(err, { silentErrors })
        if (isHandleError && typeof isHandleError === 'object' && 'isUnauthorized' in isHandleError && isHandleError.isUnauthorized) {
            throw isHandleError;
        }
        if (!isHandleError) throw err
    }
}